import React, { Dispatch, memo, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { OfferType } from '@/types/srp'
import { Container, Divider, DrawerV2, Typography } from '@cleartrip/ct-design-components'
import { isEmpty } from '@/utils/general/browserHelper'
import CopyIcon from '@/assets/icons/CopyIcon'
import Image from 'next/image'
import DefaultPricing from '@/assets/icons/defaultPricing'
import DefaultBank from '@/assets/icons/defaultBank'
import { CloseIcon } from '@/assets'
import { ravenSDKSrpCouponTrigger } from '@/analytics/raven/ravenSDKHelper'
import { useAppSelector } from '@/redux/store/hooks'
import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { useRouter } from 'next/router'
import { processQueryParams } from '@/utils/srp'
import { RAVEN_EVENT_NAME } from '@/analytics/raven/ravenConstants'
import { QueryParamsData } from '@/types/srp'

type CouponDrawerType = {
    srpCouponList: OfferType[]
    setShowDrawer: Dispatch<SetStateAction<boolean>>
    showDrawer: boolean
    couponClicked: string
}

type CouponItemProps = {
    couponData: OfferType
    index: number
    showTerms: boolean
    setShowTermsAndConditionsIndex: Dispatch<SetStateAction<number | null>>
    handleCopyCoupon: (code: string) => void
    readmoreClick: (link: string) => void
}

const CouponItem: React.FC<CouponItemProps> = ({
    couponData,
    index,
    showTerms,
    setShowTermsAndConditionsIndex,
    handleCopyCoupon,
    readmoreClick
}) => {
    const {
        MERCH_DESC,
        MERCH_SUBDESC,
        MERCH_ICON,
        TYPE,
        MERCH_CODE,
        MERCH_TNC: termsCondtions,
        MERCH_LINK
    } = couponData

    return (
        <Container className="flex flex-column flex-center ba-solid bc-line-500 mb-6 br-16 py-4 px-6">
            <Container className="flex flex-middle mb-4 ticket" onClick={() => handleCopyCoupon(MERCH_CODE)}>
                {MERCH_ICON ? (
                    <Image src={MERCH_ICON} alt="icon" height={24} width={24} />
                ) : TYPE === 'PBO' ? (
                    <DefaultBank />
                ) : (
                    <DefaultPricing />
                )}
                <div className="flex flex-middle ml-3">
                    <Typography variant="HM3">{MERCH_CODE}</Typography>
                    <CopyIcon className="ml-2 c-pointer" />
                </div>
            </Container>

            <Typography variant="HM2" className="mb-1">
                {MERCH_DESC}
            </Typography>
            <Typography variant="P2">{MERCH_SUBDESC}</Typography>
            <Divider borderStyle="dashed" className="my-3" color="#E6E6E6" />

            {!showTerms ? (
                <Container onClick={() => setShowTermsAndConditionsIndex(index)}>
                    <Typography variant="B1" color="link" className="c-pointer">
                        Know more
                    </Typography>
                </Container>
            ) : (
                !isEmpty(termsCondtions) &&
                Array.isArray(termsCondtions) && (
                    <>
                        <Typography variant="HM4">Key terms and conditions</Typography>
                        <Container className="px-5">
                            {termsCondtions.map((item, idx) => (
                                <Typography variant="P2" className="pt-2" key={idx}>
                                    {item}
                                </Typography>
                            ))}
                            <Container className="flex flex-baseline">
                                <Typography variant="P2">Additional T&Cs may apply</Typography>
                                <Container onClick={() => readmoreClick(MERCH_LINK)}>
                                    <Typography variant="L2" color="link" className="pt-2 ml-1 c-pointer">
                                        Read more
                                    </Typography>
                                </Container>
                            </Container>
                        </Container>
                    </>
                )
            )}

            {showTerms && (
                <Container onClick={() => setShowTermsAndConditionsIndex(null)}>
                    <Typography variant="L1" color="link" className="c-pointer mt-3">
                        Hide details
                    </Typography>
                </Container>
            )}
        </Container>
    )
}
const MemoizedCouponItem = memo(CouponItem)

const CouponDrawer: React.FC<CouponDrawerType> = ({ srpCouponList, showDrawer, setShowDrawer, couponClicked }) => {
    const [showTermsAndConditionsIndex, setShowTermsAndConditionsIndex] = useState<number | null>(null)
    const [selectedCouponData, setSelectedCouponData] = useState<string | null>(couponClicked)
    const { searchData} = useAppSelector(getSrpSearchData)
    const router = useRouter()
    const data = router.query as unknown as QueryParamsData
    const processedData = processQueryParams(router, data)
    
    useEffect(() => {
        if (couponClicked) {
            setSelectedCouponData(couponClicked)
        }
    }, [couponClicked])
    
    useEffect(() => {
        if (showDrawer) {
            document.body.classList.add('stopScrolling')
        }
        return () => {
            document.body.classList.remove('stopScrolling')
        }
    }, [showDrawer])

    const handleCouponClick = () => {
        const ravenPayload = {
            action_name: "srp_coupons_strip_sheet_close_click",
            action_type: 'click',
            property: 'srp_page',
            ct_coupon_applied: selectedCouponData || ''
        }
        ravenSDKSrpCouponTrigger(RAVEN_EVENT_NAME.A_UI_ACTION, searchData, processedData, { ...ravenPayload })
    }

    const onDrawerClose = () => {
        setShowDrawer(false)
        handleCouponClick()
    }

    const handleCouponCopyClick = useCallback((code: string) => {
        if(isEmpty(searchData)){
            return
        }
        const ravenPayload = {
            action_name: "srp_coupons_strip_coupon_copy_click",
            action_type: 'click',
            property: 'srp_page',
            ct_coupon_applied: code
        }
        ravenSDKSrpCouponTrigger(RAVEN_EVENT_NAME.A_UI_ACTION, searchData, processedData, { ...ravenPayload })
    }, [searchData, processedData])

    const handleCopyCoupon = useCallback((code: string) => {
        try {
            navigator.clipboard.writeText(code)
            setSelectedCouponData(code)
            handleCouponCopyClick(code)
        } catch (e) {
            console.error(e)
        }
    }, [handleCouponCopyClick])

    const readmoreClick = useCallback((link: string) => {
        window.open(link, '_blank')
    }, [])

    return (
        <DrawerV2
            direction="right"
            showOverlay={true}
            isOpen={showDrawer}
            handleClose={onDrawerClose}
            atTop={true}
            onExitEnd={() => {}}
        >
            <Container className="srp-coupon-drawer bg-white flex flex-column" style={{ width: '600px', height: '100vh' }}>
                <Container className="pl-6 pt-4 mb-6">
                    {/* Close Drawer */}
                    <Container onClick={onDrawerClose}>
                        <CloseIcon className="c-pointer mb-4" />
                    </Container>
                    <Typography variant="HM1" className="mb-2">
                        All Coupons
                    </Typography>
                    <Typography variant="P1" color="subheading">
                        Apply the coupon and get great discounts on your flight tickets.
                    </Typography>
                </Container>
                <Divider borderStyle="dashed" color="#E6E6E6" />
                <Container className="pt-6 px-6 flex flex-column flex-1 oy-scroll">
                    {srpCouponList.map((couponData, index) => (
                        <MemoizedCouponItem
                            key={index}
                            couponData={couponData}
                            index={index}
                            showTerms={showTermsAndConditionsIndex === index}
                            setShowTermsAndConditionsIndex={setShowTermsAndConditionsIndex}
                            handleCopyCoupon={handleCopyCoupon}
                            readmoreClick={readmoreClick}
                        />
                    ))}
                </Container>
            </Container>
        </DrawerV2>
    )
}

export default CouponDrawer
